<template>
  <div class="RadioInput">
    <div
      class="RadioInput__Control"
      :class="{
        'order-last': reversed,
      }"
    >
      <button
        class="w-6 h-6 border rounded-full border-primary-A900 flex items-center justify-center flex-shrink-0"
        type="button"
        :class="{
          'border-primary-900': !disabled,
          'border-grey-100 pointer-events-none': disabled,
          'order-last': reversed,
        }"
        @click="onChange"
      >
        <div
          class="w-3 h-3 rounded-full"
          :class="{
            'bg-coloration-secondary__left-to-right': checked && !disabled,
            'bg-green-50': checked && disabled,
          }"
        ></div>
      </button>

      <div class="flex flex-1 items-center space-x-4">
        <label
          v-if="label || $slots.default || $slots.labelDescription"
          :for="id"
          class="flex justify-between w-full cursor-pointer"
        >
          <div class="w-full ml-2" :class="{ 'opacity-50': disabled }">
            <slot v-if="$slots.default" name="default" />
            <span v-else>{{ label }}</span>
          </div>
          <slot name="labelDescription" />
        </label>

        <slot name="description" />
      </div>

      <input :id="id" v-model="fieldValue" :checked="checked" :name="name" type="radio" class="hidden" />
    </div>
    <label v-if="$slots.after" :for="id" class="flex flex-col cursor-pointer">
      <slot name="after" />
    </label>
  </div>
</template>

<script setup lang="ts">
import { get as getPath, isEqual } from 'lodash-es';

// const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  id: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    default: undefined,
  },
  name: {
    type: String,
    default: '',
    required: true,
  },
  value: {
    type: null,
    default: '',
  },
  modelValue: {
    type: null,
    default: '',
  },
  rules: {
    type: [String, Object],
    default: '',
  },
  showErrors: {
    type: Boolean,
    default: true,
  },
  trackBy: {
    type: String,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  reversed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'error', value: string | undefined): void;
}>();

const {
  value: fieldValue,
  setValue,
  errorMessage,
} = useField(
  () => props.name,
  {},
  {
    type: 'radio',
    checkedValue: props.value,
  },
);

watch(errorMessage, value => {
  emit('error', value);
});

const checked = computed(() => {
  if (props.trackBy) {
    return isEqual(getPath(fieldValue.value, props.trackBy), getPath(props.value, props.trackBy));
  }

  return fieldValue.value === props.value;
});

function onChange() {
  if (props.trackBy && getPath(fieldValue.value, props.trackBy) === getPath(props.value, props.trackBy)) {
    setValue(null);
    return;
  }
  setValue(props.value);
}
</script>

<style lang="postcss" scoped>
/* purgecss start ignore */
.RadioInput {
  &__Control {
    @apply flex items-center;
  }
}
/* purgecss end ignore */
</style>
